import React from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import ROUTES from '../consts/paths'
import DashboardLayout from '../layouts/DashboardLayout'
import ReportsPage from './ReportsPageV2'
import { PrivateRoute } from './../components/PrivateRoute'
import MemoizedFooterOutlet from './../components/WithFooterOutlet'
import SurveyPage from './SurveyPage'
import SurveyCompletedPage from './SurveyCompletedPage'
import NotFoundPage from './NotFoundPage'
import AdminSurveyListPage from './AdminSurveyListPage'
import ReportHierarchyPage from './ReportHierarchyPage'
import AcademicUnitsPage from './AcademicUnitsPage'
import AdminSurveyPage from './AdminSurveyPage'
import { ProjectPage } from './ProjectPage'
import StudentDashboardPage from './StudentDashboardPage'
import InstructorDashboardPage from './InstructorDashboardPage'
import MemoizedHeaderFooterLayout from './../layouts/HeaderFooterLayout/index'
import ProjectCourse from './ProjectCourse'
import FacultyAndStaff from './FacultyAndStaff'
import Users from './Students'
import { RoleBaseRoute } from '../components/RoleBaseRoute'
import TermsPage from './TermsPage'
import NoPermissions from './NoPermissions'
import CourseCatalog from './CourseCatalog'
import LogoutPage from './LogoutPage'
import LoadingPageV2 from './LoadingPageV2'
import TermSectionsAndEnrollments from './TermSectionsAndEnrollments'
import TaskPage from './TaskPage'
import Attributes from './Attributes'
import InstitutionalSettingsPage from './InstitutionalSettings/InstitutionalSettingsPage'
import QrcodePage from './Qrcode'
import SurveyPreviewPage from './SurveyPreview'
import { AdminViewerDashboard } from './AdminViewerDashboard'
import AcademicUnitAndInstructorAddedQuestions from './AcademicUnitAndInstructorAddedQuestions'
import PublicLayout from '../layouts/PublicLayout'
import { SurveyCompletedPagePublic } from './SurveyCompletePagePublic'
import { SavedComments } from './SavedComments/SavedComments'
import { LoginWrapper } from './LoginPage/LoginWrapper'
import { useAppRoutesPermissionsAndRoles } from './useAppRoutesPermissionsAndRoles'
import EmailPage from './EmailPage'
import SisSyncLogsPage from './SisSyncLogsPage'
import SisSyncRunDetailPage from './SisSyncRunDetailPage'
import EdlinkLogin from './LoginPage/EdlinkLogin'
import { useAuthenticatedContext } from '../hooks/AuthenticatedContext'

const AppRoutes: React.FC = (): JSX.Element => {
  const { isAuthed, isLoading } = useAuthenticatedContext()

  const { shouldShowLoading } = useAppRoutesPermissionsAndRoles()
  return (
    <>
      {shouldShowLoading && <LoadingPageV2 />}
      <div>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path={`${ROUTES.PUBLIC_SURVEY_PAGE}/:tenantToken/:distributionId`} element={<SurveyPage />} />
            <Route path={`${ROUTES.PUBLIC_SURVEY_PAGE}/:id?`} element={<SurveyPage />} />
          </Route>
          <Route path={ROUTES.SURVEY_COMPLETED_PUBLIC} element={<SurveyCompletedPagePublic />} />
          <Route element={<PrivateRoute />}>
            <Route index element={<Navigate to={'/'} />} />
            <Route path={ROUTES.ADMIN_PATH} element={<DashboardLayout />}>
              <Route index element={<Navigate to={ROUTES.SETTINGS} />} />
              <Route path={ROUTES.TASKS} element={<TaskPage />} />
              <Route path={ROUTES.FACULTY_AND_STAFF} element={<RoleBaseRoute element={<FacultyAndStaff />} />} />
              <Route path={ROUTES.STUDENTS} element={<RoleBaseRoute element={<Users />} />} />
              <Route path={`${ROUTES.REPORTING_HIERARCHY}/:id?`} element={<RoleBaseRoute element={<ReportHierarchyPage />} />} />
              <Route path={`${ROUTES.ACADEMIC_UNITS}`} element={<RoleBaseRoute element={<AcademicUnitsPage />} />} />
              <Route path={ROUTES.SURVEYS} element={<RoleBaseRoute element={<AdminSurveyPage />} />} />
              <Route path={`${ROUTES.SURVEY_LIST}/:id?`} element={<RoleBaseRoute element={<AdminSurveyListPage />} />} />
              <Route path={`${ROUTES.COURSE_AND_ENROLLMENT}/:id?`} element={<RoleBaseRoute element={<ProjectCourse />} />} />
              <Route path={ROUTES.DISTRIBUTION_SERIES} element={<RoleBaseRoute element={<ProjectPage />} />} />
              <Route path={ROUTES.TERMS} element={<RoleBaseRoute element={<TermsPage />} />} />
              <Route path={ROUTES.COURSE_CATALOG} element={<RoleBaseRoute element={<CourseCatalog />} />} />
              <Route path={ROUTES.INSTITUTION_SETTINGS} element={<RoleBaseRoute element={<InstitutionalSettingsPage />} />} />
              <Route path={ROUTES.PERMISSION_MANAGEMENT} element={<Navigate to={ROUTES.INSTITUTION_SETTINGS} replace />} />
              <Route path={`${ROUTES.TERMS_DETAILS}/:id`} element={<RoleBaseRoute element={<TermSectionsAndEnrollments />} />} />
              <Route path={`${ROUTES.ATTRIBUTES}`} element={<RoleBaseRoute element={<Attributes />} />} />
              <Route path={`${ROUTES.ADMIN_VIEWER_DASHBOARD}`} element={<RoleBaseRoute element={<AdminViewerDashboard />} />} />
              <Route path={`${ROUTES.ACADEMIC_UNIT_ADDED_QUESTIONS}`} element={<RoleBaseRoute element={<AcademicUnitAndInstructorAddedQuestions />} />} />
              <Route path={`${ROUTES.QR_CODE}/:id?`} element={<RoleBaseRoute element={<QrcodePage />} />} />
              <Route path={ROUTES.EMAIL} element={<RoleBaseRoute element={<EmailPage />} />} />
              <Route path={ROUTES.SIS_SYNC_LOGS} element={<RoleBaseRoute element={<SisSyncLogsPage />} />} />
              <Route path={ROUTES.SIS_SYNC_RUN_DETAILS} element={<RoleBaseRoute element={<SisSyncRunDetailPage />} />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            <Route element={<MemoizedFooterOutlet />}>
              <Route path={ROUTES.SURVEY_COMPLETED} element={<RoleBaseRoute element={<SurveyCompletedPage />} />} />
            </Route>
            <Route element={<MemoizedHeaderFooterLayout isTabMenu={false} />}>
              <Route path={`${ROUTES.SURVEY_PAGE}/:id?`} element={<RoleBaseRoute element={<SurveyPage />} />} />
            </Route>

            <Route element={<MemoizedHeaderFooterLayout />}>
              <Route index element={<Navigate to={ROUTES.INSTRUCTOR_SURVEYS} />} />
              <Route path={`${ROUTES.INSTRUCTOR_SURVEYS}/:id?`} element={<RoleBaseRoute element={<InstructorDashboardPage />} />} />
              <Route path={`${ROUTES.SAVED_COMMENTS}`} element={<RoleBaseRoute element={<SavedComments />} />} />

              <Route path={`${ROUTES.REPORTS}/:id?`} element={<RoleBaseRoute element={<ReportsPage />} />} />
              <Route path={ROUTES.STUDENT_SURVEYS} element={<RoleBaseRoute element={<StudentDashboardPage />} />} />
              <Route path={`${ROUTES.SURVEY_PREVIEW}/:slotId?/:sectionId`} element={<RoleBaseRoute element={<SurveyPreviewPage />} />} />
              <Route path="*" element={<NotFoundPage />} />
              <Route path={ROUTES.PERMISSION_DENIED} element={<NoPermissions />} />
            </Route>
          </Route>

          <Route element={<MemoizedFooterOutlet />}>
            <Route path={ROUTES.LOGOUT} element={<LogoutPage />} />
            <Route path={ROUTES.LOGIN} element={<LoginWrapper />} />
            <Route path={ROUTES.EDLINK_LOGIN} element={<EdlinkLogin />} />
          </Route>

        </Routes>
      </div>
    </>
  )
}

export default AppRoutes
